import {
  Component,
  Inject,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import {
  CommonModule,
  isPlatformBrowser,
  ViewportScroller,
} from '@angular/common';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './core/header/header.component';
import { FooterComponent } from './core/footer/footer.component';
import { LangDefinition, TranslocoService } from '@jsverse/transloco';
import { cookiesStorage } from '@jsverse/transloco-persist-lang';
import { SpinnerComponent } from './core/shared/loading/spinner/spinner.component';
import { InfoPopUpComponent } from './core/shared/pop-up/info-pop-up/info-pop-up.component';
import {
  LoadingService,
  PopUpService,
  RecaptchaLoaderService,
  loadCookieYes,
} from 'lib-shared';
import { WhatsappBtnComponent } from './core/shared/buttons/whatsapp-btn/whatsapp-btn.component';
import { ScrollToTopBtnComponent } from './core/shared/buttons/scroll-to-top-btn/scroll-to-top-btn.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    FooterComponent,
    HeaderComponent,
    InfoPopUpComponent,
    RouterOutlet,
    SpinnerComponent,
    ScrollToTopBtnComponent,
    WhatsappBtnComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  loadingService = inject(LoadingService);
  popUpService = inject(PopUpService);

  textDir: string = 'ltr';
  isLoading: boolean = false;

  constructor(
    private translate: TranslocoService,
    private route: ActivatedRoute,
    private scroller: ViewportScroller,
    private recaptchaLoader: RecaptchaLoaderService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (this.platformId === 'browser') {
      this.verifyLanguage();
    }
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        await this.recaptchaLoader.loadBothRecaptchas();
      } catch (error) {
        console.error('Error loading reCAPTCHAs:', error);
      }

      // Load cookie consent script
      try {
        loadCookieYes('e988c0b28be4fd26088063e2');
      } catch (error) {
        console.error('CookieYes script failed to load', error);
      }

      // Google Tag Manager
      try {
        const gtmScript = document.createElement('script');
        gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-TJ6TN4R2');`;
        document.head.appendChild(gtmScript);
      } catch (error) {
        console.error('Google Tag Manager script failed to load', error);
      }

      // Meta Pixel
      try {
        const pixelScript = document.createElement('script');
        pixelScript.innerHTML = `!function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1822210541958842'); fbq('track', 'PageView');`;
        document.head.appendChild(pixelScript);
      } catch (error) {
        console.error('Meta Pixel script failed to load', error);
      }
    }
  }

  // Verify user languague
  verifyLanguage() {
    const supportedLangs =
      this.translate.getAvailableLangs() as LangDefinition[];

    this.route.queryParams.subscribe((params) => {
      const lang = params['lang'];

      if (lang && supportedLangs.find((s) => s.id == lang)) {
        this.translate.setActiveLang(lang);
        localStorage.setItem('preferredLang', lang);
      } else {
        const storedLanguage =
          localStorage.getItem('preferredLang') ||
          cookiesStorage().getItem('translocoLang');

        if (
          storedLanguage &&
          supportedLangs.find((s) => s.id == storedLanguage)
        ) {
          this.translate.setActiveLang(storedLanguage);
        } else {
          const browserLang = navigator.language.split('-')[0];
          if (supportedLangs.find((s) => s.id == browserLang)) {
            this.translate.setActiveLang(browserLang);
            localStorage.setItem('preferredLang', browserLang);
          }
        }
      }
    });

    this.translate.langChanges$.subscribe((lang: string) => {
      if (lang == 'he') {
        this.textDir = 'rtl';
      } else {
        this.textDir = 'ltr';
      }
    });
  }

  onActivate(event: any) {
    if (this.platformId === 'browser') {
      window.scroll(0, 0);
    }
  }

  onClosePopUp() {
    this.popUpService.closePopUp();
  }

  ngAfterViewInit(): void {
    this.scroller.scrollToPosition([0, 0]);
  }
}
